import React from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  DocumentMagnifyingGlassIcon,
} from 'components/icons';

/**
 * Renders the compliance-agent's eligibility verdict.
 *
 * Shape is agent-defined and untyped end to end, so every field is optional and
 * the caller keeps a plain-text path for anything unrecognised.
 */
export interface CompliancePayload {
  eligible_to_bid?: boolean;
  compliance_score?: number;
  summary?: string;
  hard_fails?: Array<{ requirement?: string; reason?: string; remediable?: boolean; remediation?: string }>;
  warnings?: Array<{ requirement?: string; concern?: string; action?: string }>;
  missing_evidence?: Array<string | { requirement?: string; note?: string }>;
}

export function isCompliancePayload(v: any): v is CompliancePayload {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  return (
    typeof v.eligible_to_bid === 'boolean' ||
    Array.isArray(v.hard_fails) ||
    typeof v.compliance_score === 'number'
  );
}

const ComplianceResult: React.FC<{ data: CompliancePayload }> = ({ data }) => {
  const fails = data.hard_fails ?? [];
  const warnings = data.warnings ?? [];
  const missing = data.missing_evidence ?? [];
  const eligible = data.eligible_to_bid === true;
  const score = typeof data.compliance_score === 'number' ? data.compliance_score : null;

  return (
    <div className="space-y-5">
      {/* Verdict. A hard fail is the single most important thing on this
          screen — a contractor should not spend a week on a bid they cannot
          legally win, so it leads and it is unambiguous. */}
      {typeof data.eligible_to_bid === 'boolean' && (
        <div
          className={`rounded-lg border p-4 ${
            eligible ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
          }`}
        >
          <div className="flex items-start gap-3">
            {eligible ? (
              <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" aria-hidden="true" />
            ) : (
              <XCircleIcon className="h-6 w-6 text-red-600 flex-shrink-0" aria-hidden="true" />
            )}
            <div className="flex-1">
              <p className={`font-semibold ${eligible ? 'text-green-800' : 'text-red-800'}`}>
                {eligible ? 'Eligible to bid' : 'Not currently eligible to bid'}
              </p>
              {!eligible && fails.length > 0 && (
                <p className="text-sm text-red-700 mt-0.5">
                  {fails.length} blocking {fails.length === 1 ? 'requirement' : 'requirements'}
                  {fails.every((f) => f.remediable) ? ' — all appear fixable' : ''}
                </p>
              )}
            </div>
            {score !== null && (
              <div className="text-right flex-shrink-0">
                <p className="text-2xl font-bold text-content tabular-nums">{score}</p>
                <p className="text-[10px] text-content-muted">compliance score</p>
              </div>
            )}
          </div>
        </div>
      )}

      {data.summary && <p className="text-sm text-content-muted leading-relaxed">{data.summary}</p>}

      {fails.length > 0 && (
        <div>
          <h4 className="flex items-center gap-1.5 text-sm font-semibold text-content mb-2">
            <XCircleIcon className="h-4 w-4 text-red-500" aria-hidden="true" />
            Blocking requirements
            <span className="text-xs font-normal text-content-muted">({fails.length})</span>
          </h4>
          <ul className="space-y-3">
            {fails.map((f, i) => (
              <li key={i} className="rounded-md border border-red-200 bg-red-50/50 p-3">
                <p className="text-sm font-medium text-content">{f.requirement}</p>
                {f.reason && <p className="text-sm text-content-muted mt-1">{f.reason}</p>}
                {f.remediation && (
                  <p className="text-sm text-content mt-2">
                    <span className="font-semibold text-green-700">How to fix: </span>
                    {f.remediation}
                  </p>
                )}
                {f.remediable === false && (
                  <p className="text-xs font-semibold text-red-700 mt-2">
                    Not fixable before this deadline
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {warnings.length > 0 && (
        <div>
          <h4 className="flex items-center gap-1.5 text-sm font-semibold text-content mb-2">
            <ExclamationTriangleIcon className="h-4 w-4 text-amber-600" aria-hidden="true" />
            Watch items
            <span className="text-xs font-normal text-content-muted">({warnings.length})</span>
          </h4>
          <ul className="space-y-2">
            {warnings.map((w, i) => (
              <li key={i} className="text-sm">
                <p className="font-medium text-content">{w.requirement}</p>
                {w.concern && <p className="text-content-muted">{w.concern}</p>}
                {w.action && (
                  <p className="text-content mt-0.5">
                    <span className="font-semibold text-amber-700">Do: </span>
                    {w.action}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {missing.length > 0 && (
        <div>
          <h4 className="flex items-center gap-1.5 text-sm font-semibold text-content mb-2">
            <DocumentMagnifyingGlassIcon className="h-4 w-4 text-content-muted" aria-hidden="true" />
            Evidence still needed
          </h4>
          <ul className="space-y-1.5">
            {missing.map((m, i) => (
              <li key={i} className="flex gap-2 text-sm text-content-muted">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-content-subtle flex-shrink-0" />
                <span>{typeof m === 'string' ? m : m.requirement || m.note}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ComplianceResult;
